import dayjs from 'dayjs';
import flatMap from 'lodash/flatMap';
import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';
import uniq from 'lodash/uniq';

import { DataTransferCostItem } from '@cast/shared-types';
import type { DateRange } from '@cast/shared-types';
import { sumDataTransferCost } from '@cast/utils';

import { getItemKey } from 'features/cluster/cost-report';
import { TopMostExpensiveGroupsChartData } from 'features/cost-report/types/costOverTime';
import { TimeSeriesEntry } from 'types/metrics';
import { TopMostExpensiveGroupDatapoint } from 'types/top-items';
import { fillGaps } from 'utils/metrics';

import { WorkloadDataTransfer } from '../../../../_types';

type GroupedTransferMetrics = DataTransferCostItem &
  Pick<WorkloadDataTransfer, 'workloadName' | 'workloadType' | 'namespace'>;

const customMerge = (
  ingress: GroupedTransferMetrics[],
  egress: GroupedTransferMetrics[]
): number => {
  const totalIngressCost = sumBy(ingress, sumDataTransferCost);
  const totalEgressCost = sumBy(egress, sumDataTransferCost);
  return totalIngressCost + totalEgressCost;
};

const prepareTimeSeries = (
  data: WorkloadDataTransfer[],
  filler: Record<string, number | null>
): TopMostExpensiveGroupDatapoint => {
  const ingressMetricsGrouped: Record<string, GroupedTransferMetrics[]> =
    groupBy(
      flatMap(
        data,
        (item) =>
          item.ingressMetrics?.map((i) => ({
            ...i,
            workloadName: item.workloadName,
            workloadType: item.workloadType,
            namespace: item.namespace,
          })) || []
      ),
      (item) => item.timestamp
    );

  const egressMetricsGrouped: Record<string, GroupedTransferMetrics[]> =
    groupBy(
      flatMap(
        data,
        (item) =>
          item.egressMetrics?.map((i) => ({
            ...i,
            workloadName: item.workloadName,
            workloadType: item.workloadType,
            namespace: item.namespace,
          })) || []
      ),
      (item) => item.timestamp
    );

  const mergedResult: Record<
    string,
    TimeSeriesEntry<{ values: Record<string, number | null> }>
  > = {};

  // Assuming ingressMetricsGrouped and egressMetricsGrouped are objects
  const keys = uniq([
    ...Object.keys(ingressMetricsGrouped),
    ...Object.keys(egressMetricsGrouped),
  ]);

  keys.forEach((timestamp) => {
    const values = { ...filler };
    const ingressValue: GroupedTransferMetrics[] =
      ingressMetricsGrouped[timestamp];
    const egressValue: GroupedTransferMetrics[] =
      egressMetricsGrouped[timestamp];

    const ingressGrouped: Record<string, GroupedTransferMetrics[]> = groupBy(
      ingressValue,
      (item) => getItemKey(item)
    );

    const egressGrouped: Record<string, GroupedTransferMetrics[]> = groupBy(
      egressValue,
      (item) => getItemKey(item)
    );

    const merged: Record<string, number> = {};

    Object.keys(ingressGrouped).forEach((id: keyof typeof ingressGrouped) => {
      merged[id] = customMerge(ingressGrouped[id], egressGrouped[id]);
    });

    Object.keys(egressGrouped).forEach((id: keyof typeof egressGrouped) => {
      merged[id] = customMerge(ingressGrouped[id], egressGrouped[id]);
    });

    const _timestamp = dayjs.utc(timestamp).subtract(1, 'day').toISOString();
    mergedResult[_timestamp] = {
      timestamp: _timestamp,
      values: {
        ...values,
        ...merged,
      },
    };
  });

  return Object.values(mergedResult);
};

export const createChartData = (
  [from, to]: DateRange,
  data: WorkloadDataTransfer[],
  filler: Record<string, number | null>
): TopMostExpensiveGroupsChartData[] => {
  const timeSeries: TopMostExpensiveGroupDatapoint = prepareTimeSeries(
    data,
    filler
  );

  const dataFilledGaps = fillGaps({
    timeSeries: timeSeries,
    dateRange: {
      from,
      to: dayjs(to).subtract(1, 'day'),
    },
    filler: { values: { ...filler } },
    fillPartial: true,
    unit: 'day',
  });

  return dataFilledGaps;
};
